/* eslint-disable max-lines-per-function */
import React from "react";
import { useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import { CircularProgress, Chip, Grid, Box } from "@mui/material";
import { graphqlErrorIndicatesForbidden } from "utils/graphql-auth-error";
import {
  SlideoutApplications as Query,
  SlideoutApplicationsVariables as QueryVars,
  SlideoutApplications_otherInterests as Interest,
} from "types/gql-types";
import { Viewer } from "components/auth/types";
import { Timestamp } from "components/timestamp";
import { CopyButton } from "components/haven-design/copy-button";
import { query } from "./queries";

type Props = { interestId: string; viewer: Viewer };

const ApplicationsPage: React.FC<Props> = ({ interestId, viewer }) => {
  const { data, loading, error } = useQuery<Query, QueryVars>(query, {
    fetchPolicy: "network-only",
    variables: { interestId },
  });

  if (loading) return <CircularProgress />;
  if (error && graphqlErrorIndicatesForbidden(error)) {
    return <Navigate to="/not-authorized" replace />;
  }

  if (error) return <div>Error loading data</div>;
  const viewerListIds = viewer.lists?.map((list) => list.id) || [];

  const getApplicationsLink = (app: Interest) =>
    `/dash/manager/lists/${app.interestListId}/p/1/interests/${app.id}/applications`;
  return (
    <Grid container>
      {data?.otherInterests?.map((app) => {
        const authorizedToUseLink = viewerListIds.includes(app.interestListId);
        const showWaitlistLink = app.updateRequest?.isActive;
        const waitlistLink = app.updateRequest?.updateUrl ?? "";

        return (
          <Grid container key={app.id} sx={{
            paddingTop: 1,
            paddingBottom: 2,
            borderBottom: "1px solid #d6d6d6",
            "&:hover": {
              background: "#f7f7f7",
            },
          }}>
            <Grid size={{ xs: 12 }} sx={{ paddingLeft: 1, paddingBottom: 0.5 }}>
              {authorizedToUseLink ? (
                <a
                  href={getApplicationsLink(app)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {app.firstName} {app.lastName} - {app.interestListName}
                </a>
              ) : (
                <span style={{ color: "black" }}>
                  {app.firstName} {app.lastName} - {app.interestListName}
                </span>
              )}
              {app.id === interestId && (
                <Chip
                  label="current application"
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ marginLeft: 1 }}
                />
              )}
            </Grid>
            <Grid size={{ md: showWaitlistLink ? 6 : 12 }} sx={{
              fontSize: "12px",
              color: "gray",
              paddingLeft: 1,
              paddingRight: 1,
            }}>
              <b>Applied:</b> &nbsp;
              <Timestamp date={new Date(app.submittedAt * 1000)} showDate /> &nbsp;
              <b>Status:</b> {app.status} <br />
              <b>Household Count:</b> {app.householdCount} &nbsp;
              <b>Household Gross Annual Income:</b> $
              {app?.householdGrossAnnualIncome?.toLocaleString()}
            </Grid>
            {showWaitlistLink && (
              <Grid container size={{ md: 6 }} sx={{
                fontSize: "12px",
                color: "gray",
                paddingLeft: 1,
                paddingRight: 1,
              }}>
                <Grid>
                  <Box sx={{ marginRight: 1, marginTop: 0.25 }}>
                    <CopyButton text={waitlistLink} size="small" withLabel />
                  </Box>
                </Grid>
                <Grid>
                  <b>Waitlist Update Link:</b><br />
                  <a href={waitlistLink} target="_blank" rel="noreferrer">{waitlistLink}</a>
                </Grid>
              </Grid>
            )}
          </Grid>
        );
      })}
    </Grid>
  );
};

export default ApplicationsPage;
